import { type KeyboardEvent, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ProgressRing } from "./ProgressRing.js";
import { Tag } from "./Tag.js";
import { Icon } from "./Icon.js";
import { setOpening } from "../lib/bookTransition.js";

/** Everything a face-out cover needs to draw itself — built by the caller from a Book + manuals. */
export interface BookCoverModel {
  /** stable key for the FLIP hand-off */
  key: string;
  title: string;
  /** 対象 line printed above the title (店舗名 / 全店 / 未整理 etc.) */
  subtitle: string;
  /** 冊数 (manuals inside) */
  count: number;
  /** average read completion 0..100 across the contents */
  pct: number;
  /** every manual inside is 修了 */
  done: boolean;
  mustRead?: boolean;
  /** any manual inside still AI-processing */
  processing?: boolean;
  /** any draft / 承認待ち inside (栞 ribbon) */
  ribbon?: boolean;
}

const REDUCE = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion:reduce)").matches;

/**
 * Face-out printed-manual cover for one 本. Visuals unchanged from the bucket era (navy book-cloth
 * binding, printed inner frame, 業務マニュアル band, giant initial glyph, .hl swipe on hover) —
 * only the data source moved from derived (store × category) buckets to REAL Book entities.
 * Opening hinges (≤30°) then routes to `to` (normally /book/:id).
 */
export function BookCover({ model, to, spine = false }: { model: BookCoverModel; to: string; spine?: boolean }) {
  const navigate = useNavigate();
  const glyph = (model.title || "本").trim().charAt(0);
  // role=button makes descendants presentational — fold every state badge into the name,
  // or SR users never hear 必読 / 進捗 / 下書きあり.
  const state = [
    model.mustRead ? "必読" : "",
    model.processing ? "AI処理中" : model.done ? "修了" : model.pct > 0 ? `読了${model.pct}%` : "",
    model.ribbon ? "下書きあり" : "",
  ]
    .filter(Boolean)
    .join("・");
  const aria = `本「${model.title}」を開く（${model.count}冊${state ? `・${state}` : ""}）`;

  function open(el: HTMLElement) {
    const r = el.getBoundingClientRect();
    setOpening({ key: model.key, rect: { top: r.top, left: r.left, width: r.width, height: r.height }, title: model.title });
    const go = () => navigate(to);
    if (REDUCE()) return go();
    el.classList.add("book--opening");
    window.setTimeout(go, 170);
  }
  function onClick(e: MouseEvent<HTMLDivElement>) {
    open(e.currentTarget);
  }
  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      open(e.currentTarget);
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={aria}
      className={`book${spine ? " book--spine" : ""}`}
      onClick={onClick}
      onKeyDown={onKeyDown}
      data-bookkey={model.key}
    >
      <span className="book__bind" aria-hidden="true" />
      {!spine ? <span className="book__band" aria-hidden="true">業務マニュアル</span> : null}
      {model.ribbon ? <span className="shiori" aria-hidden="true" title="下書き/承認待ちあり" /> : null}

      {model.processing ? (
        <span className="book__proc">
          <Tag dot color="var(--hi)">AI処理中</Tag>
        </span>
      ) : model.pct > 0 || model.done ? (
        <span className="book__ring">
          <ProgressRing pct={model.pct} done={model.done} size={24} label={model.done ? "修了" : `読了 ${model.pct}%`} />
        </span>
      ) : null}

      {!spine ? <span className="book__glyph" aria-hidden="true">{glyph}</span> : null}

      <span className="book__store">
        {model.mustRead ? <Icon name="star" size={10} aria-hidden style={{ verticalAlign: "-1px", marginRight: 4 }} /> : null}
        {model.subtitle}
      </span>
      <span className="book__title jp">{model.title}</span>

      {!spine ? (
        <span className="book__foot">
          {model.mustRead ? <Tag hi>必読</Tag> : <span />}
          <Tag mono className="book__count">{model.count}冊</Tag>
        </span>
      ) : null}
    </div>
  );
}
